import type { ILearningGrainRepository } from '@/core/domain/learning/grain/ILearningGrainRepository.ts'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import { LearningGrain, LearningGrainInput } from '@/core/domain/learning/grain/LearningGrain.ts'
import type { ILearningGrainURLOptions } from '@/core/domain/learning/grain/ILearningGrainURLOptions.ts'
import { LearningGrainURLOptions } from '@/core/domain/learning/grain/LearningGrainURLOptions.ts'
import type { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import {
  type UploadedSupportFile,
  UploadedSupportFileSchema
} from '@/core/infrastructure/schemas/learning/support/UploadedSupportFileSchema.ts'

// Un fichier de 10 Mo peut dépasser le timeout par défaut (10 s) sur une connexion lente
const UPLOAD_TIMEOUT_MS = 120000

export class LearningGrainRepository implements ILearningGrainRepository {
  async fetchLearningGrain(
    courseId: number,
    moduleId: number,
    grainId: number,
    options?: ILearningGrainURLOptions
  ): Promise<LearningGrain> {
    let url = `learning/courses/${courseId}/modules/${moduleId}/grains/${grainId}`

    if (options) {
      const normalizedOptions = LearningGrainURLOptions.normalizeOptions(options)
      const urlOptions = new LearningGrainURLOptions(normalizedOptions).getUrlOptions()
      url += urlOptions
    }

    const response = await apiService.get(url)

    return LearningGrain.create(response.data)
  }

  async fetchAllLearningGrains(
    courseId: number,
    moduleId: number,
    options?: ILearningGrainURLOptions
  ): Promise<LearningGrain[]> {
    let url = `learning/courses/${courseId}/modules/${moduleId}/grains`

    if (options) {
      const normalizedOptions = LearningGrainURLOptions.normalizeOptions(options)
      const urlOptions = new LearningGrainURLOptions(normalizedOptions).getUrlOptions()
      url += urlOptions
    }

    const response: any = await apiService.get(url)

    return response.data.map((it: any) => LearningGrain.create(it))
  }

  async updateLearningGrain(
    courseId: number,
    moduleId: number,
    id: number,
    grain: LearningGrainInput
  ): Promise<LearningGrain> {
    const url = `learning/courses/${courseId}/modules/${moduleId}/grains/${id}`

    const response = await apiService.put(url, grain)

    return LearningGrain.create(response.data)
  }

  async updateLearningGrainStatus(
    courseId: number,
    moduleId: number,
    id: number,
    status: LearningStatus.Values
  ): Promise<LearningGrain> {
    const url = `learning/courses/${courseId}/modules/${moduleId}/grains/${id}/status`

    const response = await apiService.put(url, { status })

    return LearningGrain.create(response.data)
  }

  async createLearningGrain(
    courseId: number,
    moduleId: number,
    grain: LearningGrainInput
  ): Promise<LearningGrain> {
    const url = `learning/courses/${courseId}/modules/${moduleId}/grains`

    const response = await apiService.post(url, grain)

    return LearningGrain.create(response.data)
  }

  async uploadSupportFile(
    courseId: number,
    moduleId: number,
    grainId: number,
    file: File
  ): Promise<UploadedSupportFile> {
    const url = `learning/courses/${courseId}/modules/${moduleId}/grains/${grainId}/supports/files`

    const formData = new FormData()
    formData.append('file', file)

    // Retirer le Content-Type JSON par défaut : le navigateur pose la frontière multipart
    const response = await apiService.post(url, formData, {
      headers: { 'Content-Type': undefined },
      timeout: UPLOAD_TIMEOUT_MS
    })

    return UploadedSupportFileSchema.parse(response.data)
  }
}
